import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Fade,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    Zoom,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    createAdminPayableOrder,
    getAdminDeliveryQuote,
    getAdminShippingRate,
    searchCustomersForAdminOrderCreate,
    type AdminOrderCustomerSearchResult,
} from "../api/orderApi";
import {
    searchProductsForAdminOrderCreate,
    type OrderProductSearchResult,
    type OrderProductVariantSearchResult,
    type WholesaleBreak,
} from "../api/orderProductSearchApi";
import { useGoogleAddressAutocomplete } from "../hooks/useGoogleAddressAutocomplete";
import { formatPhoneInput } from "../utils/phone";

// The unit price for a wholesale schedule at a given quantity: the highest
// break whose min_qty <= quantity, falling back to the entry price. Mirrors the
// backend's pickBreakRow so the previewed price matches what gets charged.
function breakPriceAt(
    breaks: WholesaleBreak[] | null | undefined,
    fallback: number,
    quantity: number
): number {
    if (!breaks || !breaks.length) return fallback;
    const q = Number(quantity) || 1;
    let price = breaks[0].price;
    for (const b of breaks) {
        if (Number(b.min_qty) <= q) price = b.price;
        else break;
    }
    return Number(price);
}

type DraftItem = {
    product_id: number;
    variant_id: number | null;
    product_name: string;
    variant_name?: string | null;
    // Allow a transient empty string while the admin is editing the field;
    // normalized back to a number on blur and on submit.
    quantity: number | string;
    price: number | string;
    available_stock?: number;
    // Retail catalog price + this line's wholesale schedule, so the price can be
    // recomputed when the quantity or the order's channel changes.
    retail_price: number;
    wholesale_breaks?: WholesaleBreak[] | null;
    wholesale_price?: number | null;
    // True once the admin hand-edits this line's price (e.g. a free
    // replacement). Auto-repricing then leaves the line alone, and the backend
    // charges the entered price verbatim.
    price_overridden?: boolean;
};

export default function AdminCreateOrderPage() {
    const [customer, setCustomer] = useState({
        id: null as number | null,
        name: "",
        email: "",
        phone: "",
        shipping_address: "",
        shipping_address2: "",
        city: "",
        state: "",
        zip_code: "",
        country: "CA",
    });

    const [fulfillmentMethod, setFulfillmentMethod] =
        useState<"shipping" | "local_delivery">("shipping");

    const [shippingCost, setShippingCost] = useState("0");
    const [deliveryFee, setDeliveryFee] = useState("0");
    // Order-level shipping/delivery override guards. Once an admin manually edits
    // the amount, we stop letting the default rate/quote prefill clobber it.
    // Reset intentionally when the fulfillment method changes (see Method onChange).
    const [isShippingOverridden, setIsShippingOverridden] = useState(false);
    const [isDeliveryOverridden, setIsDeliveryOverridden] = useState(false);
    const [note, setNote] = useState("");
    // "Create customer account and email password setup link" — only has effect
    // for a brand-new customer (no existing user is selected/found by email).
    const [createAccount, setCreateAccount] = useState(false);

    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<OrderProductSearchResult[]>([]);
    const [items, setItems] = useState<DraftItem[]>([]);
    const [customerQuery, setCustomerQuery] = useState("");
    const [customerSearching, setCustomerSearching] = useState(false);
    const [customerResults, setCustomerResults] = useState<AdminOrderCustomerSearchResult[]>([]);
    const [selectedCustomer, setSelectedCustomer] =
        useState<AdminOrderCustomerSearchResult | null>(null);
    // Whether this order is priced at wholesale. Auto-on when a wholesale
    // customer is selected; the admin can toggle it back to retail per order.
    // Meaningless (and hidden) for a non-wholesale customer.
    const [wholesaleMode, setWholesaleMode] = useState(true);
    const customerIsWholesale = !!selectedCustomer?.is_wholesale;
    const orderWholesale = customerIsWholesale && wholesaleMode;
    const wholesaleTierId = selectedCustomer?.wholesale_tier_id ?? null;
    const addressInputRef = useRef<HTMLInputElement | null>(null);
    const [fulfillmentNotice, setFulfillmentNotice] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [createdOrder, setCreatedOrder] = useState<any>(null);
    const [error, setError] = useState("");

    // Manual discount OR coupon (mutually exclusive).
    const [discountKind, setDiscountKind] = useState<"none" | "percentage" | "fixed" | "coupon">("none");
    const [discountValue, setDiscountValue] = useState<number | "">("");
    const [couponCode, setCouponCode] = useState("");

    const subtotal = useMemo(() => {
        return items.reduce(
            (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
            0
        );
    }, [items]);

    // Preview discount for the manual cases; a coupon's exact value is computed
    // server-side on create, so it isn't previewed here.
    const discountAmount = useMemo(() => {
        if (discountKind === "percentage") return subtotal * (Number(discountValue || 0) / 100);
        if (discountKind === "fixed") return Math.min(subtotal, Number(discountValue || 0));
        return 0;
    }, [discountKind, discountValue, subtotal]);

    const total = useMemo(() => {
        return Math.max(
            0,
            subtotal -
                discountAmount +
                (fulfillmentMethod === "shipping"
                    ? Number(shippingCost || 0)
                    : Number(deliveryFee || 0))
        );
    }, [subtotal, discountAmount, fulfillmentMethod, shippingCost, deliveryFee]);

    const duplicateItem = (index: number) => {
        setItems((prev) => {
            const next = [...prev];
            next.splice(index + 1, 0, { ...prev[index], quantity: 1 });
            return next;
        });
    };

    useEffect(() => {
        const run = async () => {
            setFulfillmentNotice("");

            if (fulfillmentMethod === "shipping") {
                // Respect a manual order-level override — do not clobber it.
                if (isShippingOverridden) return;
                try {
                    const rate = await getAdminShippingRate();
                    const freeMinimum = Number(rate.free_shipping_minimum || 0);
                    const flatRate = Number(rate.shipping_rate || 0);

                    const nextShipping =
                        freeMinimum > 0 && subtotal >= freeMinimum ? 0 : flatRate;

                    setShippingCost(nextShipping.toFixed(2));
                } catch (err) {
                    console.error(err);
                    setFulfillmentNotice("Could not auto-fetch shipping rate.");
                }
                return;
            }

            if (fulfillmentMethod === "local_delivery") {
                // Respect a manual order-level override — do not clobber it.
                if (isDeliveryOverridden) return;
                if (!customer.city.trim()) {
                    setDeliveryFee("0");
                    setFulfillmentNotice("Enter a city to calculate local delivery.");
                    return;
                }

                try {
                    const quote = await getAdminDeliveryQuote({
                        city: customer.city,
                        subtotal,
                    });

                    if (!quote.eligible) {
                        setDeliveryFee("0");
                        setFulfillmentNotice(quote.message || "Local delivery is not available for this city.");
                        return;
                    }

                    setDeliveryFee((Number(quote.fee_cents || 0) / 100).toFixed(2));
                } catch (err) {
                    console.error(err);
                    setFulfillmentNotice("Could not auto-fetch delivery fee.");
                }
            }
        };

        const t = window.setTimeout(run, 300);
        return () => window.clearTimeout(t);
    }, [fulfillmentMethod, subtotal, customer.city, isShippingOverridden, isDeliveryOverridden]);

    useEffect(() => {
        const run = async () => {
            if (query.trim().length < 2) {
                setResults([]);
                return;
            }

            setSearching(true);
            try {
                const res = await searchProductsForAdminOrderCreate(
                    query,
                    orderWholesale ? wholesaleTierId : null
                );
                setResults(res);
            } catch (err) {
                console.error(err);
            } finally {
                setSearching(false);
            }
        };

        const t = window.setTimeout(run, 300);
        return () => window.clearTimeout(t);
    }, [query, orderWholesale, wholesaleTierId]);

    useEffect(() => {
        const run = async () => {
            if (customerQuery.trim().length < 2) {
                setCustomerResults([]);
                return;
            }

            setCustomerSearching(true);

            try {
                const res = await searchCustomersForAdminOrderCreate(customerQuery);
                setCustomerResults(res);
            } catch (err) {
                console.error(err);
            } finally {
                setCustomerSearching(false);
            }
        };

        const t = window.setTimeout(run, 300);
        return () => window.clearTimeout(t);
    }, [customerQuery]);

    const handleAddressSelect = useCallback((addr: any) => {
        setCustomer((prev) => ({
            ...prev,
            shipping_address: addr.street || addr.formatted,
            city: addr.city,
            state: addr.state,
            zip_code: addr.postalCode,
            country: addr.country,
        }));
    }, []);

    useGoogleAddressAutocomplete(addressInputRef, handleAddressSelect);

    const selectCustomer = (foundCustomer: AdminOrderCustomerSearchResult) => {
        setSelectedCustomer(foundCustomer);
        setCustomerQuery("");
        setCustomerResults([]);
        // Existing customer selected → no account to create.
        setCreateAccount(false);
        // A wholesale customer defaults to wholesale pricing for the order.
        setWholesaleMode(true);

        setCustomer({
            id: foundCustomer.id,
            name: foundCustomer.name || foundCustomer.full_name || "",
            email: foundCustomer.email || "",
            phone: foundCustomer.phone || foundCustomer.address_phone || "",
            shipping_address: foundCustomer.address || "",
            shipping_address2: "",
            city: foundCustomer.city || "",
            state: foundCustomer.state || "",
            zip_code: foundCustomer.zip_code || "",
            country: foundCustomer.country || "CA",
        });
    };

    // Unit price for a line at a given quantity and channel. Wholesale uses the
    // schedule (break for the qty) when one exists, else falls back to retail —
    // mirroring the backend's resolveUnitPrice.
    const linePriceFor = (
        line: Pick<DraftItem, "retail_price" | "wholesale_breaks" | "wholesale_price">,
        qty: number,
        wholesale: boolean
    ): number => {
        const hasSchedule =
            line.wholesale_price != null ||
            (line.wholesale_breaks?.length ?? 0) > 0;
        if (wholesale && hasSchedule) {
            return breakPriceAt(
                line.wholesale_breaks,
                Number(line.wholesale_price ?? line.retail_price),
                qty
            );
        }
        return line.retail_price;
    };

    const addVariant = (
        product: OrderProductSearchResult,
        variant: OrderProductVariantSearchResult
    ) => {
        if (Number(variant.available_stock || 0) <= 0) {
            setError("This variant has no available online stock.");
            return;
        }

        setError("");

        setQuery("");
        setResults([]);

        const existingIndex = items.findIndex(
            (item) => item.variant_id === variant.id
        );

        if (existingIndex >= 0) {
            setItems((prev) =>
                prev.map((item, index) => {
                    if (index !== existingIndex) return item;
                    // Coerce in case the field is mid-edit (e.g. an empty string).
                    const nextQty = Math.min(
                        (Number(item.quantity) || 0) + 1,
                        Number(item.available_stock || (Number(item.quantity) || 0) + 1)
                    );
                    return {
                        ...item,
                        quantity: nextQty,
                        // Reprice at the new qty unless the admin overrode this line.
                        price: item.price_overridden
                            ? item.price
                            : linePriceFor(item, nextQty, orderWholesale),
                    };
                })
            );
            return;
        }

        const retailPrice = Number(variant.price || 0);
        const line = {
            retail_price: retailPrice,
            wholesale_breaks: variant.wholesale_breaks ?? null,
            wholesale_price: variant.wholesale_price ?? null,
        };

        setItems((prev) => [
            ...prev,
            {
                product_id: product.product_id,
                variant_id: variant.id,
                product_name: product.product_name,
                variant_name: variant.variant_name,
                quantity: 1,
                price: linePriceFor(line, 1, orderWholesale),
                available_stock: Number(variant.available_stock || 0),
                ...line,
                price_overridden: false,
            },
        ]);
    };

    // Quantity: at least 1, never above available stock (when known).
    const normalizeQtyValue = (raw: number | string, stock?: number): number => {
        const n = Number(raw);
        let qty = !Number.isFinite(n) || n < 1 ? 1 : n;
        if (stock && stock > 0) qty = Math.min(qty, stock);
        return qty;
    };

    // Price: at least 0.
    const normalizePriceValue = (raw: number | string): number => {
        const n = Number(raw);
        return !Number.isFinite(n) || n < 0 ? 0 : n;
    };

    // Store the raw field value (including a temporary "") while the admin types.
    // Editing the price marks the line overridden so auto-repricing leaves it be.
    const setItemField = (
        index: number,
        field: "quantity" | "price",
        value: string
    ) => {
        setItems((prev) =>
            prev.map((item, i) =>
                i === index
                    ? {
                          ...item,
                          [field]: value,
                          ...(field === "price" ? { price_overridden: true } : {}),
                      }
                    : item
            )
        );
    };

    const blurNormalizeQty = (index: number) => {
        setItems((prev) =>
            prev.map((item, i) => {
                if (i !== index) return item;
                const qty = normalizeQtyValue(item.quantity, item.available_stock);
                return {
                    ...item,
                    quantity: qty,
                    // A quantity break may change the wholesale unit price — reprice
                    // unless the admin set a manual price on this line.
                    price: item.price_overridden
                        ? item.price
                        : linePriceFor(item, qty, orderWholesale),
                };
            })
        );
    };

    const blurNormalizePrice = (index: number) => {
        setItems((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, price: normalizePriceValue(item.price) } : item
            )
        );
    };

    const removeItem = (index: number) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    // Flip the order between wholesale and retail pricing, repricing every line
    // that the admin hasn't manually overridden.
    const applyChannel = (wholesale: boolean) => {
        setWholesaleMode(wholesale);
        setItems((prev) =>
            prev.map((item) =>
                item.price_overridden
                    ? item
                    : {
                          ...item,
                          price: linePriceFor(item, Number(item.quantity) || 1, wholesale),
                      }
            )
        );
    };

    const submit = async () => {
        setError("");
        setCreatedOrder(null);

        if (!customer.name.trim() || !customer.email.trim()) {
            setError("Customer name and email are required.");
            return;
        }

        if (!items.length) {
            setError("Add at least one item.");
            return;
        }

        setSubmitting(true);

        try {
            // Match the customer checkout pattern: there is no dedicated line-2
            // column, so fold the second address line into shipping_address with a
            // newline (exactly like fc-frontend's payloadBuilder).
            const line2 = customer.shipping_address2?.trim();
            const composedAddress = line2
                ? `${customer.shipping_address}\n${line2}`
                : customer.shipping_address;

            // Only a brand-new customer (none selected/found) can have an account
            // created. Backend independently re-checks by email and never creates
            // for an existing user, so this is just the UI-side gate.
            const wantsAccount = !customer.id && createAccount;

            const res = await createAdminPayableOrder({
                customer: {
                    name: customer.name,
                    email: customer.email,
                    phone: customer.phone,
                    shipping_address: composedAddress,
                    city: customer.city,
                    state: customer.state,
                    zip_code: customer.zip_code,
                    country: customer.country,
                },
                items: items.map((item) => ({
                    product_id: item.product_id,
                    variant_id: item.variant_id,
                    // Always send safe numeric values (covers fields left mid-edit).
                    quantity: normalizeQtyValue(item.quantity, item.available_stock),
                    price: normalizePriceValue(item.price),
                    price_overridden: !!item.price_overridden,
                })),
                fulfillment_method: fulfillmentMethod,
                payment_method: "payment_link",
                shipping_cost: fulfillmentMethod === "shipping" ? Number(shippingCost || 0) : 0,
                delivery_fee: fulfillmentMethod === "local_delivery" ? Number(deliveryFee || 0) : 0,
                note,
                create_account: wantsAccount,
                // Wholesale customer → send the channel (toggle on = wholesale, off
                // = retail). Non-wholesale customer → omit (backend defaults retail).
                ...(customerIsWholesale ? { wholesale: wholesaleMode } : {}),
                ...(discountKind === "coupon" && couponCode.trim()
                    ? { coupon_code: couponCode.trim() }
                    : (discountKind === "percentage" || discountKind === "fixed") &&
                      Number(discountValue) > 0
                    ? { discount: { type: discountKind, value: Number(discountValue) } }
                    : {}),
            });

            setCreatedOrder(res);
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to create payable order.");
        } finally {
            setSubmitting(false);
        }
    };

    const copyPaymentLink = async () => {
        if (!createdOrder?.payment_url) return;
        await navigator.clipboard.writeText(createdOrder.payment_url);
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1200, mx: "auto" }}>
            <Stack spacing={2}>
                {/* Header */}
                <Typography variant="h5" fontWeight={700}>
                    Create Manual Order
                </Typography>

                {/* Alerts */}
                {error && <Alert severity="error">{error}</Alert>}

                {createdOrder && (
                    <Alert severity="success">
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            spacing={1}
                            justifyContent="space-between"
                        >
                            <Stack spacing={0.25}>
                                <Typography variant="body2">
                                    Order #{createdOrder.order_number} created. Payment link sent.
                                </Typography>
                                {createdOrder.account_created && (
                                    <Typography variant="caption" color="text.secondary">
                                        {createdOrder.setup_email_sent
                                            ? "Customer account created — a password setup link was emailed to the customer."
                                            : "Customer account created, but the setup email could not be sent. Ask the customer to use “Forgot password” to receive a new link."}
                                    </Typography>
                                )}
                            </Stack>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={copyPaymentLink}
                                sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                            >
                                Copy Link
                            </Button>
                        </Stack>
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {/* Left Column */}
                    <Grid item xs={12} md={7}>
                        <Stack spacing={2}>
                            {/* Customer Card */}
                            <Card variant="outlined">
                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                        <PersonOutlineIcon fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Customer
                                        </Typography>
                                    </Stack>

                                    <TextField
                                        label="Search customer"
                                        value={customerQuery}
                                        onChange={(e) => setCustomerQuery(e.target.value)}
                                        size="small"
                                        fullWidth
                                        placeholder="Name, email, or phone"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ mb: 1.5 }}
                                    />

                                    {customerSearching && (
                                        <CircularProgress size={20} sx={{ my: 1 }} />
                                    )}

                                    {customerResults.length > 0 && (
                                        <Stack spacing={1} sx={{ mb: 1.5 }}>
                                            {customerResults.map((foundCustomer) => (
                                                <Card key={foundCustomer.id} variant="outlined">
                                                    <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            spacing={1}
                                                        >
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                                    <Typography variant="body2" fontWeight={600}>
                                                                        {foundCustomer.name || foundCustomer.email}
                                                                    </Typography>
                                                                    {foundCustomer.is_wholesale && (
                                                                        <Chip
                                                                            size="small"
                                                                            color="primary"
                                                                            label={`Wholesale${
                                                                                foundCustomer.wholesale_tier_name
                                                                                    ? ` · ${foundCustomer.wholesale_tier_name}`
                                                                                    : ""
                                                                            }`}
                                                                            sx={{ height: 18, fontSize: "0.65rem" }}
                                                                        />
                                                                    )}
                                                                </Stack>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {foundCustomer.email}
                                                                    {foundCustomer.phone ? ` · ${foundCustomer.phone}` : ""}
                                                                </Typography>
                                                            </Box>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => selectCustomer(foundCustomer)}
                                                            >
                                                                Use
                                                            </Button>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Stack>
                                    )}

                                    {selectedCustomer && (
                                        <Alert severity="success" sx={{ mb: 1.5 }}>
                                            Using: {selectedCustomer.name || selectedCustomer.email}
                                        </Alert>
                                    )}

                                    {customerIsWholesale && (
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                mb: 1.5,
                                                borderColor: orderWholesale ? "primary.main" : "divider",
                                                bgcolor: orderWholesale ? "primary.50" : "transparent",
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                spacing={1}
                                                flexWrap="wrap"
                                            >
                                                <Stack spacing={0.25}>
                                                    <Chip
                                                        size="small"
                                                        color="primary"
                                                        variant={orderWholesale ? "filled" : "outlined"}
                                                        label={`Wholesale · ${
                                                            selectedCustomer?.wholesale_tier_name ?? "Tier"
                                                        }${
                                                            selectedCustomer?.wholesale_discount_pct
                                                                ? ` −${Number(
                                                                      selectedCustomer.wholesale_discount_pct
                                                                  )}%`
                                                                : ""
                                                        }`}
                                                        sx={{ fontWeight: 600, alignSelf: "flex-start" }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {orderWholesale
                                                            ? "Lines priced at wholesale (tier + quantity breaks)."
                                                            : "Priced at retail for this order."}
                                                    </Typography>
                                                </Stack>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={wholesaleMode}
                                                            onChange={(e) => applyChannel(e.target.checked)}
                                                        />
                                                    }
                                                    label="Wholesale pricing"
                                                    labelPlacement="start"
                                                />
                                            </Stack>
                                        </Paper>
                                    )}

                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Name"
                                                value={customer.name}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({ ...prev, name: e.target.value }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Email"
                                                type="email"
                                                value={customer.email}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({ ...prev, email: e.target.value }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Phone"
                                                type="tel"
                                                inputProps={{ inputMode: "tel" }}
                                                value={customer.phone}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({ ...prev, phone: formatPhoneInput(e.target.value) }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Address"
                                                inputRef={addressInputRef}
                                                value={customer.shipping_address}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({
                                                        ...prev,
                                                        shipping_address: e.target.value,
                                                    }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Apartment, Suite, Unit (optional)"
                                                value={customer.shipping_address2}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({
                                                        ...prev,
                                                        shipping_address2: e.target.value,
                                                    }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="City"
                                                value={customer.city}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({ ...prev, city: e.target.value }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Province"
                                                value={customer.state}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({ ...prev, state: e.target.value }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Postal"
                                                value={customer.zip_code}
                                                onChange={(e) =>
                                                    setCustomer((prev) => ({ ...prev, zip_code: e.target.value }))
                                                }
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Only for a brand-new customer (none selected from search) */}
                                    {!customer.id && (
                                        <FormControlLabel
                                            sx={{ mt: 1.5, alignItems: "flex-start" }}
                                            control={
                                                <Checkbox
                                                    checked={createAccount}
                                                    onChange={(e) => setCreateAccount(e.target.checked)}
                                                    size="small"
                                                    sx={{ pt: 0.25 }}
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        Create customer account and email password setup link
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Creates a customer login for this email (if one doesn’t already
                                                        exist) and emails a secure link to set their password.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    )}
                                </CardContent>
                            </Card>

                            {/* Products Card */}
                            <Card variant="outlined">
                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                                        Products
                                    </Typography>

                                    <TextField
                                        label="Search products"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        size="small"
                                        fullWidth
                                        placeholder="Name, variant, or SKU"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    {searching && (
                                        <CircularProgress size={20} sx={{ mt: 1 }} />
                                    )}

                                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                                        {results.map((product) => (
                                            <Card key={product.product_id} variant="outlined">
                                                <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {product.product_name}
                                                    </Typography>
                                                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                        {(product.variants || []).map((variant) => (
                                                            <Stack
                                                                key={variant.id}
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems="center"
                                                                sx={{
                                                                    py: 0.5,
                                                                    px: 1,
                                                                    bgcolor: 'action.hover',
                                                                    borderRadius: 1,
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        {variant.variant_name || "Default"}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {orderWholesale && variant.wholesale_price != null ? (
                                                                            <>
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{ color: "primary.main", fontWeight: 600 }}
                                                                                >
                                                                                    ${Number(variant.wholesale_price).toFixed(2)} ws
                                                                                </Box>{" "}
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{ textDecoration: "line-through", opacity: 0.6 }}
                                                                                >
                                                                                    ${Number(variant.price || 0).toFixed(2)}
                                                                                </Box>{" "}
                                                                                · Stock: {Number(variant.available_stock || 0)}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                ${Number(variant.price || 0).toFixed(2)} · Stock:{" "}
                                                                                {Number(variant.available_stock || 0)}
                                                                            </>
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={Number(variant.available_stock || 0) <= 0}
                                                                    onClick={() => addVariant(product, variant)}
                                                                    sx={{ minWidth: 0, px: 1 }}
                                                                >
                                                                    Add
                                                                </Button>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Fulfillment Card */}
                            <Card variant="outlined">
                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                                        Fulfillment
                                    </Typography>

                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <TextField
                                                select
                                                label="Method"
                                                value={fulfillmentMethod}
                                                onChange={(e) => {
                                                    // Changing method intentionally re-recalculates the
                                                    // default amount, so clear any manual override.
                                                    setIsShippingOverridden(false);
                                                    setIsDeliveryOverridden(false);
                                                    setFulfillmentMethod(
                                                        e.target.value as "shipping" | "local_delivery"
                                                    );
                                                }}
                                                size="small"
                                                fullWidth
                                            >
                                                <MenuItem value="shipping">Shipping</MenuItem>
                                                <MenuItem value="local_delivery">Local Delivery</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label={fulfillmentMethod === "shipping" ? "Shipping" : "Delivery"}
                                                type="number"
                                                value={fulfillmentMethod === "shipping" ? shippingCost : deliveryFee}
                                                onChange={(e) => {
                                                    // Manual edit becomes an order-level override that the
                                                    // default rate/quote prefill will no longer clobber.
                                                    if (fulfillmentMethod === "shipping") {
                                                        setShippingCost(e.target.value);
                                                        setIsShippingOverridden(true);
                                                    } else {
                                                        setDeliveryFee(e.target.value);
                                                        setIsDeliveryOverridden(true);
                                                    }
                                                }}
                                                size="small"
                                                fullWidth
                                                inputProps={{ min: 0, step: "0.01" }}
                                                helperText={
                                                    (fulfillmentMethod === "shipping"
                                                        ? isShippingOverridden
                                                        : isDeliveryOverridden)
                                                        ? "Order-level override — won’t change default settings."
                                                        : "Auto-filled from default. Edit to override for this order only."
                                                }
                                            />
                                        </Grid>
                                        {fulfillmentNotice && (
                                            <Grid item xs={12}>
                                                <Alert severity="info" sx={{ py: 0 }}>
                                                    <Typography variant="caption">{fulfillmentNotice}</Typography>
                                                </Alert>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Note"
                                                value={note}
                                                onChange={(e) => setNote(e.target.value)}
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={2}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>

                    {/* Right Column - Summary */}
                    <Grid item xs={12} md={5}>
                        <Card
                            variant="outlined"
                            sx={{
                                position: { md: "sticky" },
                                top: { md: 16 },
                            }}
                        >
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                                    Order Summary ({items.length} {items.length === 1 ? 'item' : 'items'})
                                </Typography>

                                {items.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                                        No items added
                                    </Typography>
                                )}

                                <Stack spacing={1}>
                                    {items.map((item, index) => (
                                        <Box
                                            key={`${item.variant_id}-${index}`}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1,
                                                p: 1,
                                            }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="body2" fontWeight={600} noWrap>
                                                        {item.product_name}
                                                    </Typography>
                                                    {item.variant_name && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.variant_name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Stack direction="row" spacing={0}>
                                                    <Tooltip title="Duplicate line (e.g. add a free replacement)">
                                                        <IconButton
                                                            onClick={() => duplicateItem(index)}
                                                            size="small"
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton
                                                        onClick={() => removeItem(index)}
                                                        size="small"
                                                        color="error"
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>

                                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                <Grid item xs={5}>
                                                    <TextField
                                                        label="Qty"
                                                        type="number"
                                                        value={item.quantity}
                                                        onChange={(e) =>
                                                            setItemField(index, "quantity", e.target.value)
                                                        }
                                                        onBlur={() => blurNormalizeQty(index)}
                                                        size="small"
                                                        fullWidth
                                                        inputProps={{
                                                            min: 1,
                                                            max: item.available_stock || undefined,
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <TextField
                                                        label="Price"
                                                        type="number"
                                                        value={item.price}
                                                        onChange={(e) =>
                                                            setItemField(index, "price", e.target.value)
                                                        }
                                                        onBlur={() => blurNormalizePrice(index)}
                                                        size="small"
                                                        fullWidth
                                                        inputProps={{ min: 0, step: "0.01" }}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                                                Line total: ${(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    ))}

                                    {items.length > 0 && (
                                        <>
                                            <Divider />

                                            {/* Discount / coupon (mutually exclusive) */}
                                            <Stack spacing={1}>
                                                <TextField
                                                    select
                                                    size="small"
                                                    label="Discount"
                                                    value={discountKind}
                                                    onChange={(e) => {
                                                        setDiscountKind(e.target.value as any);
                                                        setDiscountValue("");
                                                        setCouponCode("");
                                                    }}
                                                    fullWidth
                                                >
                                                    <MenuItem value="none">No discount</MenuItem>
                                                    <MenuItem value="percentage">Percentage off (%)</MenuItem>
                                                    <MenuItem value="fixed">Amount off ($)</MenuItem>
                                                    <MenuItem value="coupon">Coupon code</MenuItem>
                                                </TextField>

                                                {(discountKind === "percentage" || discountKind === "fixed") && (
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        label={discountKind === "percentage" ? "Percent off" : "Amount off"}
                                                        value={discountValue}
                                                        onChange={(e) =>
                                                            setDiscountValue(e.target.value === "" ? "" : Number(e.target.value))
                                                        }
                                                        inputProps={{ min: 0, step: discountKind === "percentage" ? 1 : 0.01 }}
                                                        InputProps={
                                                            discountKind === "percentage"
                                                                ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                                                                : { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                                                        }
                                                        fullWidth
                                                    />
                                                )}

                                                {discountKind === "coupon" && (
                                                    <TextField
                                                        size="small"
                                                        label="Coupon code"
                                                        value={couponCode}
                                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                        helperText="Validated when the order is created."
                                                        fullWidth
                                                    />
                                                )}
                                            </Stack>

                                            <Stack spacing={0.5}>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="body2">Subtotal</Typography>
                                                    <Typography variant="body2" fontWeight={600}>${subtotal.toFixed(2)}</Typography>
                                                </Box>
                                                {(discountKind === "percentage" || discountKind === "fixed") && discountAmount > 0 && (
                                                    <Box display="flex" justifyContent="space-between">
                                                        <Typography variant="body2" color="success.main">Discount</Typography>
                                                        <Typography variant="body2" fontWeight={600} color="success.main">
                                                            -${discountAmount.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {discountKind === "coupon" && couponCode.trim() && (
                                                    <Box display="flex" justifyContent="space-between">
                                                        <Typography variant="body2" color="success.main">Coupon {couponCode.trim()}</Typography>
                                                        <Typography variant="body2" color="text.secondary">applied on create</Typography>
                                                    </Box>
                                                )}
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="body2">
                                                        {fulfillmentMethod === "shipping" ? "Shipping" : "Delivery"}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        ${(fulfillmentMethod === "shipping"
                                                            ? Number(shippingCost || 0)
                                                            : Number(deliveryFee || 0)
                                                        ).toFixed(2)}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                                                    <Typography variant="subtitle1" fontWeight={700}>${total.toFixed(2)}</Typography>
                                                </Box>
                                            </Stack>
                                        </>
                                    )}

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="medium"
                                        disabled={submitting || !items.length}
                                        onClick={submit}
                                        sx={{ mt: 1 }}
                                    >
                                        {submitting ? (
                                            <CircularProgress size={20} color="inherit" />
                                        ) : (
                                            "Create & Send Payment Link"
                                        )}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    );
}